import * as stylex from "@stylexjs/stylex";
import { X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import "@/design/tokens.stylex";
import { colors, layout, motion, radii, shadows, space, text } from "@/design/tokens.stylex";
import { IconButton } from "./Button";
import { useEscapeLayer } from "./useEscapeLayer";

// Detail opens in a side panel while the list stays visible (Mercury 11, 12):
// a title and close at the top, the detail in a scrolling body, and the
// conversation (comments, a composer) pinned at the foot.
//
// A side panel leaves the page usable, so it does not trap focus. It still
// takes focus when it opens, so a keyboard reader lands on what just appeared,
// and hands it back to whatever opened it when it closes. Escape closes it
// unless a menu or dialog above it takes Escape first.

const slideIn = "xw3ilb2-B";
export interface SidePanelProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Quiet actions beside close, e.g. a More menu. */
  actions?: ReactNode;
  /** Comments and a composer, pinned to the panel's foot. */
  foot?: ReactNode;
  onClose: () => void;
  size?: "md" | "lg";
  "aria-label"?: string;
  children: ReactNode;
}
export default function SidePanel({
  title,
  subtitle,
  actions,
  foot,
  onClose,
  size = "md",
  "aria-label": ariaLabel,
  children
}: SidePanelProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  // Read before the first commit, so a field inside that focuses itself on
  // mount is not mistaken for the opener.
  const [opener] = useState(() => typeof document !== "undefined" && document.activeElement instanceof HTMLElement ? document.activeElement : null);
  useEscapeLayer(onClose);
  useEffect(() => {
    const panel = panelRef.current;
    // A field that focused itself on mount (autoFocus) keeps focus.
    if (!panel?.contains(document.activeElement)) panel?.focus({
      preventScroll: true
    });
    return () => {
      // Hand focus back only when it went with the panel, not when the reader
      // has already moved on elsewhere.
      const active = document.activeElement;
      const lost = !active || active === document.body || panel?.contains(active);
      if (lost && opener?.isConnected) opener.focus({
        preventScroll: true
      });
    };
  }, [opener]);
  return <aside ref={panelRef} tabIndex={-1} role="complementary" aria-label={ariaLabel} aria-labelledby={ariaLabel ? undefined : titleId} {...{
    0: {
      className: "xixxii4 xf5e64p xv299eb x139nh9p x1p2gcem x78zum5 xdt5ytf x1wjhxqy x1h1f4i9 xmkeg23 x1y0btm7 xzbo3x7 xhrzdqn xi20zh2 xqgf3xs x1gxywl9 xt2ab0p xb3r6kr x1a2a7pz"
    },
    1: {
      className: "xixxii4 xf5e64p xv299eb x139nh9p x1p2gcem x78zum5 xdt5ytf x1h1f4i9 xmkeg23 x1y0btm7 xzbo3x7 xhrzdqn xi20zh2 xqgf3xs x1gxywl9 xt2ab0p xb3r6kr x1a2a7pz xjl8ti"
    }
  }[!!(size === "lg") << 0]}>
      <header className="x78zum5 x1cy8zhl x166cprz xh0uz7e xf8hsz2 xxgjxbq">
        <div className="x98rzlu xeuugli x67pdoy">
          <h2 id={titleId} className="x1ghz6dp xxmgfec xk50ysn xgwkh9f xj0a0fe">
            {title}
          </h2>
          {subtitle && <div className="x17isgxd x7x6yu3 x1i6qc9y">{subtitle}</div>}
        </div>
        <div className="x78zum5 x6s0dn4 xsuxsxl">
          {actions}
          <IconButton aria-label="Close" onClick={onClose}>
            <X size={16} />
          </IconButton>
        </div>
      </header>
      <div className="x98rzlu x2lwn1j x1odjw0f xf8hsz2 x1x82rsn">{children}</div>
      {foot && <footer className="x2lah0s x12yisup x1odjw0f x178xt8z x13fuv20 x13hb0vi x16orzk0 xf8hsz2 x1h1f4i9">{foot}</footer>}
    </aside>;
}

/** A titled group inside a side panel's body, split from the one above by a hairline. */
export function PanelSection({
  title,
  children
}: {
  title?: ReactNode;
  children: ReactNode;
}) {
  return <section className="x78zum5 xdt5ytf x166cprz x9end0a x178xt8z x13fuv20 xq9jyqv">
      {title && <h3 className="x1ghz6dp x7x6yu3 xk50ysn x1e3avs9">{title}</h3>}
      {children}
    </section>;
}